import React from 'react';

import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';

import { Box, Container, Tab, Tabs } from '@mui/material';

import PageSubheader from '~/components/Common/Elements/PageSubheader';
import GHADiscountSummaryTable from '~/components/GHADiscountSummary/GHADiscountSummaryTable';

import useAppNavigation from '~/hooks/useAppNavigation';

import ExclusionsPanel from './ExclusionsPanel';
import ScopeRulesPanel from './ScopeRulesPanel';
import { DISCOUNTING_SECTIONS, DiscountingSection } from './constants';

// GHA-429: the GHA Discounting page — the old discount-summary tab renamed in place (bookmarks
// keep working) plus sub-tabs for managing scope rules and exclusions without a deploy. Sub-tab
// and rule focus persist in the URL so a view is shareable; `?rule=<id>` highlights one rule and
// is the link-through target from the summary chips.
export default function GHADiscountingContainer() {
  const location = useLocation();
  const { push } = useAppNavigation();
  const searchParams = new URLSearchParams(location.search);

  const sectionParam = searchParams.get('section');
  const section: DiscountingSection = DISCOUNTING_SECTIONS.includes(sectionParam as DiscountingSection)
    ? (sectionParam as DiscountingSection)
    : 'summary';
  const focusedRuleId = searchParams.get('rule');

  const handleSectionChange = (next: DiscountingSection) => {
    const params = new URLSearchParams(location.search);
    params.set('section', next);
    params.delete('rule'); // focus belongs to the tab it was linked into
    push({ search: params.toString() });
  };

  return (
    <Container>
      <Helmet>
        <title>GHA Discounting</title>
      </Helmet>
      <PageSubheader title="GHA Discounting" />
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={section} onChange={(_, next: DiscountingSection) => handleSectionChange(next)}>
          <Tab label="Summary" value="summary" />
          <Tab label="Scope Rules" value="scope-rules" />
          <Tab label="Exclusions" value="exclusions" />
        </Tabs>
      </Box>
      {section === 'summary' && <GHADiscountSummaryTable />}
      {section === 'scope-rules' && <ScopeRulesPanel focusedRuleId={focusedRuleId} />}
      {section === 'exclusions' && <ExclusionsPanel focusedRuleId={focusedRuleId} />}
    </Container>
  );
}
